import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { schoolApi } from '../../api/school'
import { queryKeys } from '../../api/queryKeys'
import { ApiError } from '../../api/client'
import { TeacherRow } from './TeacherRow'

/**
 * Сводный список учителей школы. Содержит кнопку «Пригласить»
 * (видна только VICE_PRINCIPAL+) и таблицу с раскрываемыми строками.
 */
export function SchoolDashboard({
  schoolId,
  canInvite,
  onInvite,
}: {
  schoolId: string
  canInvite: boolean
  onInvite: () => void
}) {
  const { t } = useTranslation()
  const teachers = useQuery({
    queryKey: queryKeys.school.teachers(schoolId),
    queryFn: () => schoolApi.listTeachers(schoolId),
    retry: 1,
  })

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        {canInvite && (
          <button onClick={onInvite} className="btn-primary text-sm">
            {t('schoolDashboard.inviteTeacher')}
          </button>
        )}
        <div className="text-sm text-gray-500 ml-auto">
          {teachers.isLoading ? t('common.loading') : t('schoolDashboard.teacherCount', { n: teachers.data?.length ?? 0 })}
        </div>
      </div>

      {teachers.isError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
          {(teachers.error as ApiError).message}{' '}
          {t('schoolDashboard.noPermissionHint')}
        </div>
      )}

      {teachers.data && teachers.data.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">{t('schoolDashboard.colName')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden md:table-cell">{t('schoolDashboard.colEmail')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700">{t('schoolDashboard.colRole')}</th>
                <th className="text-left px-4 py-2 font-semibold text-gray-700 hidden lg:table-cell">{t('schoolDashboard.colRegistered')}</th>
              </tr>
            </thead>
            <tbody>
              {teachers.data.map((t) => (
                <TeacherRow key={t.id} t={t} schoolId={schoolId} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
